import React, { useState, useEffect, useRef } from 'react';
import { X, ShoppingCart, Clock, Star, Flame, Sparkles, Gift, Award, ChefHat, Percent } from 'lucide-react';

// Template configurations
const TEMPLATES = {
  elegante: {
    gradient: 'from-slate-900 via-slate-800 to-slate-900',
    accent: 'from-amber-500 to-orange-500',
    textColor: 'text-white',
    badgeColor: 'bg-amber-500',
  },
  fiesta: {
    gradient: 'from-purple-900 via-pink-800 to-purple-900',
    accent: 'from-pink-500 to-purple-500',
    textColor: 'text-white',
    badgeColor: 'bg-pink-500',
  },
  urgente: {
    gradient: 'from-red-900 via-red-800 to-orange-900',
    accent: 'from-red-500 to-orange-500',
    textColor: 'text-white',
    badgeColor: 'bg-red-500',
  },
  gourmet: {
    gradient: 'from-emerald-900 via-teal-800 to-emerald-900',
    accent: 'from-emerald-500 to-teal-500',
    textColor: 'text-white',
    badgeColor: 'bg-emerald-500',
  },
  tropical: {
    gradient: 'from-cyan-900 via-blue-800 to-cyan-900',
    accent: 'from-cyan-500 to-blue-500',
    textColor: 'text-white',
    badgeColor: 'bg-cyan-500',
  },
};

const BADGES = {
  mas_vendido: { icon: Flame, text: 'Más Vendido', color: 'bg-orange-500' },
  nuevo: { icon: Sparkles, text: 'Nuevo', color: 'bg-blue-500' },
  ultima_oportunidad: { icon: Clock, text: 'Última Oportunidad', color: 'bg-red-500' },
  chef_recomienda: { icon: ChefHat, text: 'Chef Recomienda', color: 'bg-emerald-500' },
};

const PromoPopup = ({ 
  promotion, 
  onClose, 
  onAddToCart,
  socialProofCount = 142 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const audioRef = useRef(null);

  const template = TEMPLATES[promotion?.template] || TEMPLATES.elegante;
  const badge = promotion?.badge ? BADGES[promotion.badge] : null;

  // Calculate time left
  useEffect(() => {
    if (!promotion?.end_date || !promotion?.show_countdown) return;

    const calculateTimeLeft = () => {
      const endDate = new Date(promotion.end_date);
      const now = new Date();
      const diff = endDate - now;

      if (diff <= 0) {
        return null;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds, total: diff };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [promotion?.end_date, promotion?.show_countdown]);

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      setShowConfetti(true);
      
      // Play sound if enabled
      if (promotion?.sound_enabled && audioRef.current) {
        audioRef.current.play().catch(() => {});
      }
    }, 100);

    // Hide confetti after animation
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
    };
  }, [promotion?.sound_enabled]);

  const handleAddToCart = () => {
    onAddToCart(promotion);
    onClose();
  };

  const formatPrice = (price) => {
    return `$${price?.toFixed(0) || 0}`;
  };

  const formatTimeLeft = () => {
    if (!timeLeft) return null;
    return `${String(timeLeft.hours).padStart(2, '0')}:${String(timeLeft.minutes).padStart(2, '0')}:${String(timeLeft.seconds).padStart(2, '0')}`;
  };

  const getProgressPercent = () => {
    if (!timeLeft || !promotion?.end_date || !promotion?.start_date) return 50;
    const start = new Date(promotion.start_date || Date.now() - 86400000);
    const end = new Date(promotion.end_date);
    const now = new Date();
    const total = end - start;
    const elapsed = now - start;
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };

  if (!promotion) return null;

  return (
    <>
      {/* Audio element for notification sound */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7v/////////////////////////////////" type="audio/mp3" />
      </audio>

      {/* Backdrop */}
      <div 
        className={`fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 z-[101] pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            >
              <div 
                className="w-3 h-3 rotate-45"
                style={{
                  backgroundColor: ['#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#22c55e'][Math.floor(Math.random() * 5)]
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <div 
        className={`fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-[102] 
          sm:w-full sm:max-w-lg transition-all duration-700 ease-out
          ${isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'}`}
      >
        <div className={`relative bg-gradient-to-br ${template.gradient} rounded-3xl overflow-hidden shadow-2xl border border-white/10`}>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 bg-black/30 hover:bg-black/50 rounded-full transition-colors"
            data-testid="promo-close-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Header Glow Effect */}
          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-gradient-to-r ${template.accent} blur-3xl opacity-30`} />

          {/* Badge */}
          {badge && (
            <div className={`absolute top-4 left-4 z-20 flex items-center gap-2 px-3 py-1.5 ${badge.color} rounded-full text-white text-sm font-semibold`}>
              <badge.icon className="w-4 h-4" />
              {badge.text}
            </div>
          )}

          {/* Content */}
          <div className="relative p-6 pt-16">
            
            {/* Promo Label */}
            <div className="text-center mb-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${template.accent} rounded-full`}>
                <Sparkles className="w-5 h-5 text-white" />
                <span className="text-white font-bold text-lg">PROMOCIÓN ESPECIAL</span>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Image with Ken Burns effect */}
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-6 bg-black/20">
              {promotion.image_url ? (
                <img
                  src={promotion.image_url}
                  alt={promotion.title}
                  className={`w-full h-full object-cover transition-all duration-[10000ms] ease-linear
                    ${imageLoaded ? 'scale-110' : 'scale-100'}`}
                  onLoad={() => setImageLoaded(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Gift className="w-24 h-24 text-white/30" />
                </div>
              )}
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Discount badge on image */}
              <div className="absolute bottom-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full font-bold text-lg shadow-lg">
                -{promotion.savings_percent}%
              </div>
            </div>

            {/* Product Info */}
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {promotion.title}
              </h2>
              {promotion.subtitle && (
                <p className="text-white/70 text-lg mb-2">{promotion.subtitle}</p>
              )}
              {/* Description - what's included */}
              {promotion.description && (
                <p className="text-amber-300/90 text-sm mb-4 px-4">
                  📋 {promotion.description}
                </p>
              )}
              
              {/* Pricing */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-white/50 line-through text-xl">
                  {formatPrice(promotion.original_price)}
                </span>
                <span className="text-4xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  {formatPrice(promotion.promo_price)}
                </span>
              </div>
              
              {/* Savings */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full text-green-400 text-sm font-medium">
                <Percent className="w-4 h-4" />
                Ahorras {formatPrice(promotion.savings)}
              </div>
            </div>

            {/* Countdown Timer */}
            {promotion.show_countdown && timeLeft && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <span className="text-white/70 text-sm">Termina en:</span>
                </div>
                <div className="text-3xl font-mono font-bold text-white text-center tracking-wider">
                  {formatTimeLeft()}
                </div>
                <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-gradient-to-r ${template.accent} transition-all duration-1000`}
                    style={{ width: `${getProgressPercent()}%` }}
                  />
                </div>
              </div>
            )}

            {/* CTA Button */}
            <button
              onClick={handleAddToCart}
              className={`w-full py-4 px-6 bg-gradient-to-r ${template.accent} text-white font-bold text-lg rounded-2xl
                shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                flex items-center justify-center gap-3`}
              data-testid="promo-add-to-cart-btn"
            >
              <ShoppingCart className="w-6 h-6" />
              ¡LO QUIERO!
            </button>

            {/* Social Proof */}
            {promotion.show_social_proof && (
              <div className="mt-4 flex items-center justify-center gap-2 text-white/50 text-sm">
                <div className="flex -space-x-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="w-6 h-6 rounded-full bg-white/20 border-2 border-slate-800" />
                  ))}
                </div>
                <span>{socialProofCount} personas lo pidieron esta semana</span>
              </div>
            )}

            {/* Skip Link */}
            <button
              onClick={onClose}
              className="w-full mt-4 py-3 text-white/50 hover:text-white/80 text-sm font-medium transition-colors"
            >
              Ver menú completo →
            </button>
          </div>
        </div>
      </div>

      {/* Confetti Animation Styles */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </>
  );
};

export default PromoPopup;
